import { TippyCustomzie } from "components/TippyCustomzie"
import { ApprovalStatus } from "components/activity-log/activity-log.type"
import { useBoolean } from "helpers/hooks"
import { formatDate } from "helpers/utils"
import { ReactComponent as IconInfoCircle } from "assets/images/icons/icon-info-circle.svg"
import Button from "components/Button/Button"

interface Props {
  listApprovalLogs: {
    content: ApprovalStatus
    created_at: string
    user: {
      avatar: string
      id: string
      fullname: string
      email: string
    }
  }[]
  isOwner: boolean
  handleCancellation: () => void
  handleApprove: () => void
  handleReject: () => void
  idUserInfo: string
  createdBy: string
}
export const ApprovalLogCard = (props: Props) => {
  const {
    listApprovalLogs,
    isOwner,
    handleCancellation,
    handleApprove,
    handleReject,
    idUserInfo,
    createdBy,
  } = props
  const showInfoApprovalLog = useBoolean(false)

  if (!listApprovalLogs?.length) {
    return null
  }
  const required = listApprovalLogs.filter(
    (approvalLog) => approvalLog.content === ApprovalStatus.REQUIRED
  )
  const approved = listApprovalLogs.filter(
    (approvalLog) => approvalLog.content === ApprovalStatus.APPROVED
  )
  const rejected = listApprovalLogs.filter(
    (approvalLog) => approvalLog.content === ApprovalStatus.REJECTED
  )
  const cancelled = listApprovalLogs.filter(
    (approvalLog) => approvalLog.content === ApprovalStatus.CANCELLED
  )
  const totalRequired = required.length

  const approvedUserIds = approved.map((log) => log.user.id)
  const rejectedUserIds = rejected.map((log) => log.user.id)
  const pendingUsers = required.filter(
    (log) =>
      !approvedUserIds.includes(log.user.id) &&
      !rejectedUserIds.includes(log.user.id)
  )
  const pending = pendingUsers.length

  const isUserRequired =
    pendingUsers.some((pendingLog) => pendingLog.user.id === idUserInfo) &&
    !cancelled.length

  const renderIconApproval = () => {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="8" cy="8" r="7" fill="#56A012" />
        <path
          d="M5 8L7 10L11 6"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )
  }
  const renderIconRejected = () => {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="8" cy="8" r="7" fill="#EA4545" />
        <path
          d="M6 6L10 10M10 6L6 10"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )
  }
  const renderIconPending = () => {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="8" cy="8" r="7" fill="#F7AC1B" />
        <path
          d="M8 4V8L10 10"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )
  }
  return (
    <div className="flex flex-col gap-[8px] mt-[8px] border-t border-[#E4E4E4] pt-[8px]">
      {required.length ? (
        <div className="flex items-center gap-[4px]">
          <div
            className="w-[22px] h-[22px] border border-[#e87a16] rounded-full"
            style={{
              backgroundColor: "rgba(232, 122, 22,0.5)",
            }}
          ></div>
          <p className="font-normal text-13 leading-22 color-black-111111 mr-1">
            {`Approval required: ${required
              .map(
                (approvalLog) =>
                  `${approvalLog.user.fullname} - ${approvalLog.user.email}`
              )
              .join(", ")}`}
          </p>
        </div>
      ) : null}
      <div className="flex flex-col gap-[4px]">
        <div className="flex items-center gap-[12px]">
          <div className="flex items-center gap-[4px]">
            {renderIconApproval()}
            <p className="font-normal text-13 leading-22 color-black-111111">
              Approved: {approved.length}/{totalRequired}
            </p>
          </div>
          <div className="flex items-center gap-[4px]">
            {renderIconRejected()}
            <p className="font-normal text-13 leading-22 color-black-111111">
              Rejected: {rejected.length}/{totalRequired}
            </p>
          </div>
          <div className="flex items-center gap-[4px]">
            {renderIconPending()}
            <p className="font-normal text-13 leading-22 color-black-111111">
              Pending: {pending}/{totalRequired}
            </p>
          </div>
          <TippyCustomzie
            containerClass="component-card-menu"
            placement="top"
            interactive
            arrow={false}
            animation="scale"
            visible={showInfoApprovalLog.value}
            onClickOutside={() => showInfoApprovalLog.setValue(false)}
            content={
              <div
                style={{
                  background: "#222222",
                  borderRadius: 4,
                  paddingTop: 8,
                  paddingBottom: 8,
                  minWidth: 200,
                  maxWidth: 300,
                }}
              >
                {required.map((el, index) => {
                  const approvedItem = approved.find(
                    (approvedEl) => approvedEl.user.id === el.user.id
                  )
                  const rejectedItem = rejected.find(
                    (rejectedEl) => rejectedEl.user.id === el.user.id
                  )
                  const pendingItem = pendingUsers.find(
                    (pendingEl) => pendingEl.user.id === el.user.id
                  )
                  return (
                    <div
                      key={index}
                      className="flex items-center gap-[4px] px-[12px] border-b"
                      style={{
                        borderColor:
                          index === required.length - 1
                            ? "transparent"
                            : "#333333",
                        paddingBottom:
                          index === required.length - 1 ? "0" : "8px",
                      }}
                    >
                      <div>
                        {approvedItem ? renderIconApproval() : null}
                        {rejectedItem ? renderIconRejected() : null}
                        {pendingItem ? renderIconPending() : null}
                      </div>
                      <div className="flex flex-col gap-[4px]">
                        <p className="font-normal text-13 leading-22 color-white">
                          {el.user.fullname} - {el.user.email}
                        </p>
                        {approvedItem || rejectedItem ? (
                          <p className="font-normal text-[10px] color-[#7A7A7A]">
                            {formatDate(
                              approvedItem?.created_at ||
                                rejectedItem?.created_at ||
                                "",
                              "DD MMM yyyy - HH:mm A"
                            )}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  )
                })}
              </div>
            }
            allowHTML
          >
            <div
              className="cursor-pointer"
              onClick={() => showInfoApprovalLog.setValue(true)}
            >
              <IconInfoCircle style={{ width: 22, height: 22 }} />
            </div>
          </TippyCustomzie>
        </div>
        {cancelled.length > 0 && (
          <div className="flex items-center gap-[4px] mt-[8px]">
            <span style={{ fontSize: 16 }}>🚫</span>
            <p
              className="font-bold text-13 leading-22"
              style={{
                color: "#EA4545",
              }}
            >
              Approval cancelled by {cancelled[0].user.fullname} -{" "}
              {cancelled[0].user.email} at{" "}
              {formatDate(cancelled[0].created_at, "DD MMM yyyy - HH:mm A")}
            </p>
          </div>
        )}
      </div>

      <div className="flex items-center gap-[8px] mt-[8px]">
        {isUserRequired ? (
          <>
            <Button
              title="Approve"
              sizeBtn="small"
              colorBtn="yellow"
              onClick={handleApprove}
              styleButton={{
                width: 80,
              }}
            />
            <Button
              title="Reject"
              sizeBtn="small"
              colorBtn="red"
              onClick={handleReject}
              styleButton={{
                width: 80,
              }}
            />
          </>
        ) : null}
        {(isOwner || idUserInfo === createdBy) &&
        !cancelled.length &&
        pendingUsers.length ? (
          <Button
            title="Cancellation"
            sizeBtn="small"
            colorBtn="red"
            onClick={handleCancellation}
            styleButton={{
              width: 80,
            }}
          />
        ) : null}
      </div>
    </div>
  )
}
